// Google Ads conversie: geslaagde registratie van een bedrijf.
// Vuurt exact één keer per registratie — niet bij inloggen, niet bij een tweede
// render en niet als een pagina herladen wordt na de registratie.
//
// De gtag-basis staat in /cookie-consent.js (in de <head> van elke pagina) met
// Consent Mode v2: zonder cookie-toestemming stuurt Google een cookieloze ping
// en modelleert het de conversie, dus we hoeven hier niets af te schermen.

const CONVERSION_ID = 'AW-18355470334/jLCoCJfA8NccEP7_yLBE'

// Micro-conversie "Registratie gestart (bedrijf)": vuurt zodra iemand op
// /register de rol Bedrijf kiest. Lichter signaal (waarde €5 vs €50 voor de
// afgeronde registratie) zodat Smart Bidding iets te leren heeft zolang
// volledige registraties schaars zijn.
const START_CONVERSION_ID = 'AW-18355470334/ZxSOCKHO8eAcEP7_yLBE'

// Rollen die als bedrijfsregistratie tellen. Agency staat er bewust NIET in —
// zie het openstaande punt in de campagne-afspraken.
const CONVERTING_ROLES = ['bedrijf']

// Opdracht-trechter voor Google Ads (24-09-2026). Registraties alleen waren te
// schaars voor PMax (13 conversies in 4 maanden). Waarde in Ads: gestart €15,
// geplaatst €100.
const BRIEF_START_CONVERSION_ID = 'AW-18355470334/-7NPCOqF9YIdEP7_yLBE'
const BRIEF_PLACED_CONVERSION_ID = 'AW-18355470334/C9tDCO2F9YIdEP7_yLBE'

// GA4-property (G-YGR2MCR2Q6 in /cookie-consent.js). Zelfde momenten als de
// Ads-conversies, maar voor álle rollen met account_type erbij, zodat GA4
// bedrijven en creators kan scheiden.
const GA_ID = 'G-YGR2MCR2Q6'
const gaEvent = (name, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, { ...params, send_to: GA_ID })
    }
  } catch {
    /* tracking mag nooit de flow breken */
  }
}
const adsEvent = (sendTo, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { ...params, send_to: sendTo })
    }
  } catch {
    /* tracking mag nooit de flow breken */
  }
}

// Max wachttijd voordat we alsnog doorsturen als Google niet terugbelt.
const CALLBACK_TIMEOUT_MS = 1000

// Meta Pixel: bestaat alleen na cookie-toestemming (zie /cookie-consent.js).
// Geen fbq = geen toestemming of adblocker; dan gewoon niets sturen.
// eventId = hetzelfde id als de backend meestuurt via de Conversions API
// (utils/metaCapi.js), zodat Meta browser- en server-event ontdubbelt.
const fireMetaEvent = (eventName, eventId, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', eventName, params, eventId ? { eventID: eventId } : undefined)
    }
  } catch {
    /* tracking mag nooit de flow breken */
  }
}

const sentKey = (userId) => `ugc4you:adsConversionSent:${userId || 'anon'}`

const alreadySent = (userId) => {
  try {
    return sessionStorage.getItem(sentKey(userId)) === '1'
  } catch {
    return false
  }
}

const markSent = (userId) => {
  try {
    sessionStorage.setItem(sentKey(userId), '1')
  } catch {
    /* private mode e.d. — dan liever dubbel meten dan niet meten */
  }
}

/**
 * Stuurt het conversie-event voor een nieuwe bedrijfsregistratie.
 * Resolvet zodra Google het event bevestigt, of na CALLBACK_TIMEOUT_MS.
 * Await dit vóór een redirect, anders kan het event verloren gaan.
 *
 * @param {object} user - de zojuist geregistreerde gebruiker
 * @returns {Promise<boolean>} of er daadwerkelijk een event is verstuurd
 */
export const trackSignupConversion = (user) => {
  const role = user?.role
  const userId = user?.id || user?._id

  if (onceInSession(`ugc4you:gaSignup:${userId || 'anon'}`)) {
    gaEvent('sign_up', { account_type: role || 'onbekend' })
  }

  if (!CONVERTING_ROLES.includes(role)) return Promise.resolve(false)
  if (alreadySent(userId)) return Promise.resolve(false)
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('[ads] gtag niet beschikbaar — conversie niet verstuurd')
    return Promise.resolve(false)
  }

  markSent(userId)
  fireMetaEvent('CompleteRegistration', userId ? `reg_${userId}` : undefined)

  return new Promise((resolve) => {
    let done = false
    const finish = (sent) => {
      if (done) return
      done = true
      resolve(sent)
    }

    // Vangnet: als Google niet terugbelt (adblocker, trage verbinding) gaan we
    // na een seconde alsnog verder — de gebruiker mag hier niet op wachten.
    const timer = setTimeout(() => finish(true), CALLBACK_TIMEOUT_MS)

    window.gtag('event', 'conversion', {
      send_to: CONVERSION_ID,
      event_callback: () => {
        clearTimeout(timer)
        finish(true)
      }
    })
  })
}

/**
 * Stuurt de micro-conversie zodra een bezoeker de bedrijfsregistratie start
 * (rol gekozen, formulier nog niet ingevuld). Fire-and-forget: er volgt geen
 * redirect, dus geen callback/timeout nodig. Eén keer per sessie — de
 * gebruiker is hier nog anoniem, dus dedupe kan niet op user-id.
 *
 * @param {string} role - de gekozen rol
 * @returns {boolean} of er een event is verstuurd
 */
export const trackSignupStartConversion = (role) => {
  const key = 'ugc4you:adsStartSent'

  if (onceInSession(`ugc4you:gaSignupStart:${role || 'onbekend'}`)) {
    gaEvent('sign_up_start', { account_type: role || 'onbekend' })
  }

  if (!CONVERTING_ROLES.includes(role)) return false
  try {
    if (sessionStorage.getItem(key) === '1') return false
  } catch {
    /* private mode e.d. — dan liever dubbel meten dan niet meten */
  }
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('[ads] gtag niet beschikbaar — start-conversie niet verstuurd')
    return false
  }

  try {
    sessionStorage.setItem(key, '1')
  } catch {
    /* zie boven */
  }

  window.gtag('event', 'conversion', { send_to: START_CONVERSION_ID })
  return true
}

/**
 * Brief geplaatst — het echte conversiedoel van de Meta-campagnes.
 * Aangeroepen vanuit de createProject-wrapper in services/api.js, zodat élk
 * pad (wizard, create-pagina) meetelt. Concepten tellen niet mee. Elke
 * geplaatste brief is een eigen lead; de backend stuurt hetzelfde event
 * server-side met hetzelfde event-id. Sinds 24-09-2026 ook Google Ads
 * ("Opdracht geplaatst (bedrijf)", €100; transaction_id = project-id tegen dubbeltellen) en GA4.
 */
export const trackBriefPlacedConversion = (projectId) => {
  fireMetaEvent('Lead', projectId ? `lead_${projectId}` : undefined)
  adsEvent(BRIEF_PLACED_CONVERSION_ID, projectId ? { transaction_id: `brief_${projectId}` } : {})
  gaEvent('opdracht_geplaatst', { account_type: 'bedrijf' })
}

/**
 * Soft conversions voor de Meta-campagnes op /opdracht-starten (14-09-2026).
 * Lead en CompleteRegistration zijn te schaars om het algoritme iets te leren
 * (Meta wil ±50 events/week per ad-set). Daarom een trechter met meer volume:
 *   ViewContent      = wizard geladen (elke klik uit een ad die de pixel accepteert)
 *   InitiateCheckout = eerste veld ingevuld (intentie)
 *   CompleteRegistration / Lead = bestaande events verderop in de flow
 * Eén keer per sessie; browser-pixel én server-side (CAPI) met hetzelfde event_id.
 */
const onceInSession = (key) => {
  try {
    if (sessionStorage.getItem(key) === '1') return false
    sessionStorage.setItem(key, '1')
  } catch {
    /* private mode: dan liever dubbel meten dan niet meten */
  }
  return true
}

// Server-side spiegel via de Conversions API (POST /api/track/meta).
// Nodig omdat window.fbq pas na cookie-akkoord bestaat: een bezoeker uit een
// advertentie ziet eerst de banner, dus het browser-event ging verloren
// (0 ViewContent in 28 dagen, 17-09-2026). Zelfde event_id → Meta ontdubbelt.
const API_BASE = (import.meta.env && import.meta.env.VITE_API_URL) || 'https://ugc4you.nl/api'
const newEventId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
const sendServerEvent = (event, eventId, contentName) => {
  try {
    fetch(`${API_BASE}/track/meta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({ event, eventId, contentName }),
    }).catch(() => {})
  } catch {
    /* tracking mag nooit de flow breken */
  }
}

export const trackWizardView = () => {
  if (!onceInSession('ugc4you:metaWizardView')) return false
  const eventId = newEventId('vc')
  fireMetaEvent('ViewContent', eventId, { content_name: 'opdracht-starten', content_category: 'wizard' })
  sendServerEvent('ViewContent', eventId, 'opdracht-starten')
  return true
}

// Google Ads + GA4: bedrijf begint aan een opdracht (eerste antwoord in de
// wizard, op /opdracht-starten én in de dashboard-assistent). Eén keer per sessie.
export const trackBriefStart = () => {
  if (!onceInSession('ugc4you:googleBriefStart')) return false
  adsEvent(BRIEF_START_CONVERSION_ID)
  gaEvent('opdracht_gestart', { account_type: 'bedrijf' })
  return true
}

export const trackWizardStart = () => {
  trackBriefStart()
  if (!onceInSession('ugc4you:metaWizardStart')) return false
  const eventId = newEventId('ic')
  fireMetaEvent('InitiateCheckout', eventId, { content_name: 'opdracht-starten', content_category: 'wizard' })
  sendServerEvent('InitiateCheckout', eventId, 'opdracht-starten')
  return true
}

export default trackSignupConversion
